// src/components/admin/JobForm.js
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  AppBar,
  Toolbar,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ArrowBack as BackIcon, Save as SaveIcon } from "@mui/icons-material";

const JobForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const assignedToRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    date: new Date(),
    address: "",
    description: "",
    assignedTo: "",
    status: "pending",
  });

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditing);
  const [error, setError] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [selectWidth, setSelectWidth] = useState(0);

  // Monitor the width of the select component
  useEffect(() => {
    const updateSelectWidth = () => {
      if (assignedToRef.current) {
        setSelectWidth(assignedToRef.current.clientWidth);
      }
    };

    // Initial measurement
    updateSelectWidth();

    // Update on window resize
    window.addEventListener("resize", updateSelectWidth);
    return () => window.removeEventListener("resize", updateSelectWidth);
  }, [assignedToRef.current]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch employees
        const employeesRes = await axios.get("/users/employees");
        setEmployees(employeesRes.data.employees || []);

        // If editing, fetch job details
        if (isEditing) {
          const jobRes = await axios.get(`/jobs/${id}`);
          const job = jobRes.data.job;

          setFormData({
            name: job.name,
            date: new Date(job.date),
            address: job.address,
            description: job.description || "",
            assignedTo: job.assignedTo?._id || "",
            status: job.status,
          });
        }
      } catch (error) {
        setError("Failed to load data. Please try again.");
        console.error("Error fetching data:", error);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchData();
  }, [id, isEditing]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear validation error when field is changed
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({ ...prev, date }));

    // Clear validation error when date is changed
    if (formErrors.date) {
      setFormErrors((prev) => ({ ...prev, date: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Job name is required";
    }

    if (!formData.address.trim()) {
      errors.address = "Job address is required";
    }

    if (!formData.date) {
      errors.date = "Job date is required";
    }

    if (!formData.assignedTo) {
      errors.assignedTo = "Please assign this job to an employee";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      if (isEditing) {
        // Update existing job
        await axios.put(`/jobs/${id}`, formData);
      } else {
        // Create new job
        await axios.post("/jobs", formData);
      }

      navigate("/admin");
    } catch (error) {
      setError("Failed to save job. Please try again.");
      console.error("Error saving job:", error);
      setLoading(false);
    }
  };

  const goBack = () => {
    navigate("/admin");
  };

  if (initialLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            sx={{ mr: 2 }}
            onClick={goBack}
          >
            <BackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {isEditing ? "Edit Job" : "Create New Job"}
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 4, mb: 4, px: isMobile ? 2 : 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Paper elevation={3} sx={{ p: isMobile ? 2 : 3 }}>
          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={isMobile ? 2 : 3}>
              {/* Job Name - Full Width Row */}
              <TextField
                name="name"
                label="Job Name"
                fullWidth
                variant="outlined"
                value={formData.name}
                onChange={handleChange}
                error={!!formErrors.name}
                helperText={formErrors.name}
                required
                size={isMobile ? "small" : "medium"}
              />

              {/* Job Date - Full Width Row */}
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Job Date"
                  value={formData.date}
                  onChange={handleDateChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      required
                      error={!!formErrors.date}
                      helperText={formErrors.date}
                      size={isMobile ? "small" : "medium"}
                    />
                  )}
                />
              </LocalizationProvider>

              {/* Assigned To - Full Width Row */}
              <FormControl
                fullWidth
                error={!!formErrors.assignedTo}
                required
                size={isMobile ? "small" : "medium"}
                ref={assignedToRef}
              >
                <InputLabel id="assigned-to-label">Assigned To</InputLabel>
                <Select
                  labelId="assigned-to-label"
                  id="assignedTo"
                  name="assignedTo"
                  value={formData.assignedTo}
                  label="Assigned To"
                  onChange={handleChange}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        // Set the width to match the select input
                        maxHeight: isMobile ? 200 : 300,
                        width: selectWidth > 0 ? selectWidth : undefined,
                      },
                    },
                    // Anchor the menu to the select component for better alignment
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    // Keep the menu open on scroll
                    getContentAnchorEl: null,
                  }}
                >
                  {employees.map((employee) => (
                    <MenuItem key={employee._id} value={employee._id}>
                      {employee.name}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.assignedTo && (
                  <FormHelperText>{formErrors.assignedTo}</FormHelperText>
                )}
              </FormControl>

              {/* Job Address - Full Width Row */}
              <TextField
                name="address"
                label="Job Address"
                fullWidth
                variant="outlined"
                value={formData.address}
                onChange={handleChange}
                error={!!formErrors.address}
                helperText={formErrors.address}
                required
                size={isMobile ? "small" : "medium"}
              />

              {/* Job Description - Full Width Row */}
              <TextField
                name="description"
                label="Job Description"
                fullWidth
                variant="outlined"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={isMobile ? 3 : 4}
                size={isMobile ? "small" : "medium"}
              />

              {/* Status - Full Width Row (when editing) */}
              {isEditing && (
                <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formData.status}
                    label="Status"
                    onChange={handleChange}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: isMobile ? 200 : 300,
                          width: selectWidth > 0 ? selectWidth : undefined,
                        },
                      },
                    }}
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="in-progress">In Progress</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                  </Select>
                </FormControl>
              )}

              {/* Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: 2,
                  mt: 2,
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  disabled={loading}
                  fullWidth={isMobile}
                  size={isMobile ? "medium" : "large"}
                >
                  {loading ? "Saving..." : "Save Job"}
                </Button>

                <Button
                  variant="outlined"
                  onClick={goBack}
                  fullWidth={isMobile}
                  size={isMobile ? "medium" : "large"}
                >
                  Cancel
                </Button>
              </Box>
            </Stack>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default JobForm;
